import Client from './Client';

export type RtTicket = {
    id: string;
    subject: string | null;
    status: string | null;
    queue: string | null;
    owner: string | null;
    requestor: string | null;
    created: string | null;
    lastUpdated: string | null;
    link: string | null;
    imported: boolean;
    change: { _id: string; slug: number; project: string } | null;
};

export type RtMeta = {
    enabled: boolean;
    default_post_import_status: string | null;
    statuses: string[];
    migrate_comment_marker: string;
    active_import_batch?: RtImportBatch | null;
};

export type RtImportResult = {
    ticketId: string;
    mode: 'created' | 'synced' | 'skipped' | 'error';
    reason?: string;
    error?: string;
    change?: { _id: string; slug: number; project: string; url?: string };
    sync?: { imported: number; skipped: number };
    rtCommentPosted?: boolean;
    rtCommentFailed?: string | boolean;
    rtStatusUpdated?: boolean;
    rtStatusFailed?: string | boolean;
};

export type RtImportBatchTicket = {
    ticketId: string;
    status: 'pending' | 'running' | 'created' | 'synced' | 'skipped' | 'error';
    error?: string | null;
    result?: RtImportResult | null;
};

export type RtImportBatch = {
    _id: string;
    status: 'pending' | 'running' | 'completed' | 'failed';
    total: number;
    done: number;
    failed: number;
    projectId: string;
    tickets: RtImportBatchTicket[];
    results?: RtImportResult[];
    startedAt?: string | null;
    finishedAt?: string | null;
    createdAt?: string;
    updatedAt?: string;
};

export type RtUser = {
    id: string;
    name: string;
    email: string | null;
};

export type RtQueue = {
    id: string;
    name: string;
    description: string | null;
};

class Rt extends Client {
    async _json(path: string, method: 'GET' | 'POST', query?: AnyObj, body?: AnyObj) {
        const response = method === 'GET'
            ? await this.GET(path, query)
            : await this.POST(path, body);
        const json = await response.json().catch(() => ({}));
        if (!response.ok)
            throw Object.assign(
                new Error(json?.err || response.statusText || `HTTP ${response.status}`),
                { status: response.status, batchId: json?.batchId, body: json }
            );
        return json;
    }

    meta() {
        return this._json('/plugins/rt/meta', 'GET') as Promise<RtMeta>;
    }

    listUsers(params?: AnyObj) {
        return this._json('/plugins/rt/users', 'GET', params) as Promise<{
            items: RtUser[];
            total: number;
        }>;
    }

    listQueues() {
        return this._json('/plugins/rt/queues', 'GET') as Promise<{
            items: RtQueue[];
            total: number;
        }>;
    }

    searchTickets(params: AnyObj) {
        return this._json('/plugins/rt/tickets', 'GET', params) as Promise<{
            items: RtTicket[];
            total: number;
            page: number;
            per_page: number;
            pages?: number;
            query: string;
        }>;
    }

    getTicket(id: string) {
        return this._json(`/plugins/rt/tickets/${id}`, 'GET');
    }

    /**
     * Enqueue an async import batch (HTTP 202).
     */
    importTickets(body: {
        ticketIds: string[];
        projectId: string;
        boardId?: string | null;
        columnId?: string | null;
        assigneeId?: string | null;
        notifyRt?: boolean;
        rtStatus?: string | null;
        reSync?: boolean;
        type?: string | null;
    }) {
        return this._json('/plugins/rt/import', 'POST', undefined, body) as Promise<{
            batchId: string;
            batch: RtImportBatch;
        }>;
    }

    getImportBatch(batchId: string) {
        return this._json(`/plugins/rt/import/${batchId}`, 'GET') as Promise<{
            batch: RtImportBatch;
        }>;
    }

    getActiveImportBatch() {
        return this._json('/plugins/rt/import-batches/active', 'GET') as Promise<{
            batch: RtImportBatch | null;
        }>;
    }
}

const RtApi = new Rt();
export default RtApi;
